'use strict';

import { NUMBER_SYSTEM, UNITS } from 'Constants/numbers';

export const toFixed = (number, decimals) => parseFloat(parseFloat(number).toFixed(decimals));

export const parseLocaleInteger = (stringNumber, locale) => {
    if (typeof stringNumber !== 'string') {
        return NaN;
    }

    const thousandSeparator = (11111).toLocaleString(locale).replace(/1/g, '');// eslint-disable-line no-magic-numbers
    const decimalSeparator = (1.1).toLocaleString(locale).replace(/1/g, '');// eslint-disable-line no-magic-numbers

    return parseInt(stringNumber
        .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
        .replace(new RegExp(`\\${decimalSeparator}`), '.'), 10);
};

export const formatPrice = (value, locale) => {
    if (value && locale) {
        return value.toLocaleString(locale);
    }
    return value;
};

export const getFormattedAmount = (value, currency, locale) => {
    const formattedAmount = formatPrice(value, locale);

    if (currency && currency.pre) {
        return `${currency.pre} ${formattedAmount}`;
    }
    else if (currency && currency.post) {
        return `${formattedAmount} ${currency.post}`;
    }
    return formattedAmount;
};

const POW = 10;
const ONE_HUNDRED_THOUSAND = 100000;
const DIGITS_TO_EXCLUDE = {
    indian: {
        0: UNITS.TENS_TWO_DIGIT,
        1: UNITS.HUNDREDS_THREE_DIGIT,
        other: 0
    },
    international: {
        0: UNITS.THOUSANDS_FOUR_DIGITS,
        1: UNITS.TENS_TWO_DIGIT,
        2: UNITS.HUNDREDS_THREE_DIGIT,
        other: 0
    }
};

/**
 * Get the Number System
 * @param {Number} count
 * @param {string} locale
 * @returns {NUMBER_SYSTEM}
 */
export const getNumberSystem = (count, locale) => {
    if (!(
        count && typeof count === 'number'
        && locale && typeof locale === 'string'
    )) {
        return NUMBER_SYSTEM.INTERNATIONAL;
    }

    const parsedCount = count && parseInt(count, 10);
    let numberSystem = NUMBER_SYSTEM.INDIAN;

    // As below hundred thousand both systems are same
    if (count < ONE_HUNDRED_THOUSAND) {
        return numberSystem;
    }

    const formattedWithLocale = formatPrice(parsedCount, locale);

    const numbers = formattedWithLocale.split(/[,|.]/);
    // getting the second last number from the array e.g. [4,33,888] or [433,888]
    const secondLast = numbers.length >= 2 && numbers[numbers.length - 2];

    if (secondLast) {
        numberSystem = secondLast.length === UNITS.HUNDREDS_THREE_DIGIT ? NUMBER_SYSTEM.INTERNATIONAL : NUMBER_SYSTEM.INDIAN;
    }

    return numberSystem;
};

/**
 * Get the Digits to exclude from the number to round off
 * @param {Number} count
 * @param {NUMBER_SYSTEM} numberSystem
 * @returns {Number} number of digits to exclude from the end of the number
 */
export const mostSiginificantDigitsExcludedFromRoundOff = (count, numberSystem) => {
    if (!(
        count && typeof count === 'number'
        && numberSystem && typeof numberSystem === 'string'
    )) {
        return 0;
    }
    const digitsCount = count.toString().length;

    if (digitsCount <= UNITS.HUNDREDS_THREE_DIGIT) {
        return digitsCount - 1;
    }

    const modConfig = {
        indian: digitsCount % UNITS.TENS_TWO_DIGIT,
        international: digitsCount % UNITS.HUNDREDS_THREE_DIGIT,
        other: 1
    };
    const mod = typeof modConfig[numberSystem] === 'number' ? modConfig[numberSystem] : modConfig.other;

    return DIGITS_TO_EXCLUDE[numberSystem][mod] || DIGITS_TO_EXCLUDE[numberSystem].other;
};

/**
 * Round off numbers to logic based on https://olx-autos.atlassian.net/wiki/spaces/GH/pages/3868621/Round+off+Int+Value
 * @param {Number} count
 * @param {NUMBER_SYSTEM} providedNumberSystem
 * @param {string} locale e.g. en-IN, es-AR
 * @returns {Number} rounded off number
 */
export const roundOff = (count, providedNumberSystem, locale) => {
    if (!(
        count && typeof count === 'number'
        && (providedNumberSystem || locale) && typeof (providedNumberSystem || locale) === 'string'
    )) {
        return count || 0;
    }
    const numberSystem = providedNumberSystem || getNumberSystem(count, locale);
    const countString = count.toString();
    const digitsCount = countString.length;
    const digitsToExcludeFromRoundOff = mostSiginificantDigitsExcludedFromRoundOff(count, numberSystem);
    const subCount = countString.substr(0, digitsToExcludeFromRoundOff);

    if (digitsToExcludeFromRoundOff > 0) {
        return parseInt(subCount, 10) * Math.pow(POW, digitsCount - digitsToExcludeFromRoundOff);
    }
    return count;
};

/**
 * Remove Comma's from Amount and convert it to Number
 * @param {String} amtStr
 */
export const removeCommaFromPrice = (amtStr = '') => {
    if (typeof amtStr === 'string') {
        return Number(amtStr.replace(/[\s,|\s.]+/g, ''));
    }
    return amtStr;
};
